/**
 * Frontend-only "virtual" subchannel entries.
 *
 * These are injected into the catalog at render time so Atrium-managed
 * connectors can appear on channels beyond their DB channel_key without
 * any backend/migration changes. The DB record for the underlying
 * provider (e.g. Sonobi → key "sonobi", channel_key "programmatic")
 * remains the source of truth for provisioning; this is display-only.
 */

import type { CatalogChannelView, CatalogSubchannelView } from './get-catalog'

/**
 * Map of channel_key → extra virtual subchannels to inject.
 * Entries are appended after the real DB subchannels for that channel.
 */
const VIRTUAL_SUBCHANNELS: Record<string, CatalogSubchannelView[]> = {
  tv: [
    {
      key: 'sonobi',
      channel_key: 'tv',
      name: 'Sonobi',
      logo_text: 'S',
      logo_url: null,
      description: 'JetStream — direct publisher relationships',
      default_auth_mode: 'atrium_managed',
      status: 'available',
      enabled: true,
      display_order: 99,
      created_at: '',
      updated_at: '',
      homepage_url: null,
      connection: null,
    },
  ],
}

/**
 * Returns a new channels array with virtual subchannels injected.
 * The original array is not mutated.
 */
export function applyVirtualSubchannels(
  channels: CatalogChannelView[],
): CatalogChannelView[] {
  return channels.map((ch) => {
    const extras = VIRTUAL_SUBCHANNELS[ch.key]
    if (!extras || extras.length === 0) return ch
    // Deduplicate: don't inject if the key already exists (DB added it)
    const existingKeys = new Set(ch.subchannels.map((s) => s.key))
    const toAdd = extras.filter((e) => !existingKeys.has(e.key))
    if (toAdd.length === 0) return ch
    return { ...ch, subchannels: [...ch.subchannels, ...toAdd] }
  })
}

/**
 * Variant for a single-channel view (ChannelDetailPage).
 */
export function applyVirtualSubchannelsToChannel(
  channel: CatalogChannelView,
): CatalogChannelView {
  return applyVirtualSubchannels([channel])[0]
}
